import { safeStorage } from '@patchbay/ui'
import { dehydrate, hydrate, type Query, type QueryClient } from '@tanstack/react-query'

/**
 * Cold open from the Query cache (scope §5.0 States): the last successful reads are kept in this
 * browser so a fresh tab renders the queue and cases at once, with the thin "refreshing" bar while the
 * API revalidates, never a blank page. It is a per-viewer convenience, not state:
 *  - only allow-listed key roots are written (never the session, which carries the CSRF token);
 *  - the blob names the signed-in user and is dropped when a different user signs in, on sign-out
 *    and on any 401;
 *  - it expires after 12 h (the sliding session) and on every new build;
 *  - every storage access is guarded (private windows, blocked site data).
 */

export const PERSIST_KEY = 'patchbay-query-cache'
export const PERSIST_MAX_AGE_MS = 12 * 60 * 60 * 1000
const BUILD = typeof __PATCHBAY_BUILD__ === 'string' ? __PATCHBAY_BUILD__ : 'dev'

/** Key roots safe to keep for cold open. Liveness is excluded on purpose: a stale "fresh" pill would lie. */
export const PERSISTED_ROOTS = new Set(['lanes', 'queue', 'case', 'home', 'dispatch', 'ledger', 'billing', 'knowledge', 'my-day'])

interface Blob {
  build: string
  user: string
  savedAt: number
  state: unknown
}

export function shouldPersist(query: Query): boolean {
  const root = query.queryKey[0]
  return typeof root === 'string' && PERSISTED_ROOTS.has(root) && query.state.status === 'success'
}

let currentUser: string | null = null

/** Restore before the first render. `now` is injectable for tests. */
export function restoreQueryCache(queryClient: QueryClient, now: number = Date.now()): boolean {
  const raw = safeStorage.getItem(PERSIST_KEY)
  if (!raw) return false
  try {
    const blob = JSON.parse(raw) as Blob
    if (blob.build !== BUILD || now - blob.savedAt > PERSIST_MAX_AGE_MS) {
      clearPersistedCache()
      return false
    }
    currentUser = blob.user
    // biome-ignore lint/suspicious/noExplicitAny: dehydrated state is opaque JSON
    hydrate(queryClient, blob.state as any)
    return true
  } catch {
    clearPersistedCache()
    return false
  }
}

export function clearPersistedCache(): void {
  try {
    globalThis.localStorage?.removeItem(PERSIST_KEY)
  } catch {
    /* convenience only */
  }
}

/** The shell calls this when the session resolves. A different user drops everything cached. */
export function setPersistUser(queryClient: QueryClient, userId: string | null): void {
  if (userId === currentUser) return
  if (currentUser !== null && userId !== currentUser) {
    clearPersistedCache()
    for (const q of queryClient.getQueryCache().getAll())
      if (shouldPersist(q)) queryClient.removeQueries({ queryKey: q.queryKey, exact: true })
  }
  currentUser = userId
}

export function saveQueryCache(queryClient: QueryClient, now: number = Date.now()): void {
  if (!currentUser) return
  const state = dehydrate(queryClient, { shouldDehydrateQuery: shouldPersist })
  const blob: Blob = { build: BUILD, user: currentUser, savedAt: now, state }
  safeStorage.setItem(PERSIST_KEY, JSON.stringify(blob))
}

/**
 * Subscribes to the cache and writes at most once a second. Returns an unsubscribe.
 *
 * A navigation drops a save that has not landed yet. Otherwise the timer fires while the
 * document is leaving and puts the cache back after the page cleared it, so the next
 * document paints the previous packet instead of what the server just said.
 */
export function persistQueryCache(queryClient: QueryClient): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let unloading = false
  const onPageHide = () => {
    unloading = true
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
  const onPageShow = () => {
    unloading = false
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
  }
  const unsub = queryClient.getQueryCache().subscribe((e) => {
    if (e.type !== 'updated' || timer || unloading) return
    timer = setTimeout(() => {
      timer = null
      if (unloading) return
      saveQueryCache(queryClient)
    }, 1000)
  })
  return () => {
    if (timer) clearTimeout(timer)
    unsub()
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }
}

/** Sign-out and 401: forget everything this browser kept. */
export function forgetEverything(queryClient: QueryClient): void {
  currentUser = null
  clearPersistedCache()
  queryClient.clear()
}
